/**
 * Sampling summary — read-only glance at the Style File Overview (second
 * section, right after the hero card): status, photos as a tile grid, and the
 * latest comments. No actions here — Approve/Reject/Submit/Add photo all live
 * on the full Sampling screen this links out to.
 *
 * Comments render as plain Slack-style rows (avatar · author · time · body),
 * capped at 3 with a "view more" toggle — deliberately NOT cards-inside-a-
 * tinted-panel, which reads as a broken box-in-a-box.
 *
 * @see apps/web/src/features/styles/components/samples-section.tsx — the working screen
 * @see apps/web/src/features/attachments/attachments-card.tsx — the same photo store, read here via `attachments.list`
 */
import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowRight, ImageOff } from "lucide-react";
import { StatusPill } from "@flint/ui";
import type { samples as samplesContract } from "@flint/api-contract";
import { trpc } from "../../../lib/trpc.js";
import { formatDate, formatDateTime } from "../../../lib/format.js";
import { SectionCard } from "./style-cockpit-chrome.js";

type SamplesData = samplesContract.SamplesViewWire;

const STATUS_TONE: Record<SamplesData["status"], "ok" | "sky" | "bad" | "muted"> = {
  approved: "ok",
  submitted: "sky",
  rejected: "bad",
  pending: "muted",
};
const STATUS_LABEL: Record<SamplesData["status"], string> = {
  approved: "Approved",
  submitted: "Submitted",
  rejected: "Rejected",
  pending: "Not started",
};
/** How many comments show before the "view more" toggle. */
const COMMENT_PREVIEW = 3;

export function SamplingSummaryCard({ styleId }: { styleId: string }) {
  const [expanded, setExpanded] = useState(false);
  const query = trpc.samples.getForStyle.useQuery({ styleId });
  const data = query.data;
  const photos = trpc.attachments.list.useQuery(
    { ownerType: "garment_sample_submission", ownerId: data?.latestSubmissionId ?? "" },
    { enabled: !!data?.latestSubmissionId },
  );

  const headerAction = data ? (
    <>
      <div className="flex items-center gap-2">
        <StatusPill tone={STATUS_TONE[data.status]} size="sm" dot>
          {STATUS_LABEL[data.status]}
        </StatusPill>
        {data.revisionCount > 1 ? (
          <span className="rounded-full bg-bg px-1.5 py-0.5 font-mono text-xs font-bold text-ink-2">R{data.revision}</span>
        ) : null}
        {data.submittedAt ? <span className="text-xs text-muted">Submitted {formatDate(data.submittedAt)}</span> : null}
      </div>
      <Link to="/sampling/styles/$styleId/samples" params={{ styleId }} className="flex items-center gap-1 text-xs font-semibold text-sky-700 hover:text-sky-800">
        Go to Sampling <ArrowRight size={12} />
      </Link>
    </>
  ) : null;

  // Newest-first for the preview — the latest feedback is what matters at a glance.
  const newestFirst = data ? [...data.comments].reverse() : [];
  const shownComments = expanded ? newestFirst : newestFirst.slice(0, COMMENT_PREVIEW);

  return (
    <SectionCard
      title="Sampling"
      meta="One sample · approval unlocks production"
      id="sec-sampling-summary"
      action={headerAction}
    >
      {query.isLoading ? (
        <p className="text-sm text-muted">Loading sampling…</p>
      ) : !data || data.status === "pending" ? (
        <div className="rounded-sm border border-dashed border-line bg-bg px-4 py-5">
          <p className="text-sm text-muted">No sample submitted yet.</p>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {/* Photos — tile grid, scales cleanly from 1 to a handful. */}
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted">Photos</p>
            {photos.isLoading ? (
              <p className="text-xs text-muted">Loading photos…</p>
            ) : (photos.data?.items.length ?? 0) === 0 ? (
              <div className="flex items-center gap-2 rounded-sm border border-dashed border-line bg-bg px-3 py-4 text-xs text-muted">
                <ImageOff size={14} className="shrink-0" /> No photos yet.
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-5">
                {photos.data!.items.map((p) => (
                  <a
                    key={p.id}
                    href={p.publicUrl ?? "#"}
                    target="_blank"
                    rel="noreferrer"
                    title={p.originalName}
                    className="group block aspect-square overflow-hidden rounded-md border border-line bg-bg"
                  >
                    {p.contentType.startsWith("image/") && p.publicUrl ? (
                      <img src={p.publicUrl} alt="" className="h-full w-full object-cover transition group-hover:opacity-90" />
                    ) : (
                      <span className="grid h-full w-full place-items-center px-1 text-center text-xs text-muted">{p.originalName}</span>
                    )}
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Comments — plain rows, newest first, capped at 3. */}
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted">
              Comments{data.commentCount > 0 ? ` (${data.commentCount})` : ""}
            </p>
            {data.comments.length === 0 ? (
              <p className="text-xs text-muted">No comments yet.</p>
            ) : (
              <>
                <ul className="flex flex-col gap-3">
                  {shownComments.map((c, i) => (
                    <li key={i} className="flex gap-2.5">
                      <span
                        aria-hidden
                        className={`mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full text-xs font-bold ${
                          c.authorKind === "buyer" ? "bg-sky-100 text-sky-700" : "bg-bg text-ink-2"
                        }`}
                      >
                        {c.authorKind === "buyer" ? "B" : "T"}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-baseline gap-x-2">
                          <span className="text-xs font-semibold text-ink">
                            {c.authorKind === "buyer" ? "Buyer" : "Internal"}
                          </span>
                          <span className="text-xs text-muted">{formatDateTime(c.at)}</span>
                        </div>
                        <p className="mt-0.5 whitespace-pre-wrap break-words text-xs leading-relaxed text-ink-2">
                          {c.body}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
                {data.comments.length > COMMENT_PREVIEW ? (
                  <button
                    type="button"
                    onClick={() => setExpanded(!expanded)}
                    className="mt-2.5 text-xs font-medium text-sky-700 hover:underline"
                  >
                    {expanded ? "Show less" : `View all ${data.commentCount} comments`}
                  </button>
                ) : null}
              </>
            )}
          </div>
        </div>
      )}
    </SectionCard>
  );
}
